import type {
  ConversationInvitationResource,
  ConversationInvitationStudentResource,
} from "api/generated";
import { mapAttachments, mapLinks, mapVideos } from "shared/mappers";
import type {
  ConversationInvitation,
  ConversationInvitationStudentRow,
} from "conversations/types";
import { conversationQuestionsFromDto } from "./conversationQuestion.mapper";
import { parseSchoolDateTime } from "shared/utils";

/**
 * `part` of `total` as a whole percent, rounded DOWN so a gesprek only reads 100% once every
 * leerling really is there. Zero leerlingen is 0%, not a division by zero: a just-planned
 * gesprek whose queue has not created its children yet sits there.
 */
const percentageOf = (part: number, total: number): number =>
  total === 0 ? 0 : Math.floor((part / total) * 100);

/**
 * Maps `GET api/conversation-invitations` / `.../{id}` (ConversationInvitationResource).
 *
 * The three criteria arrays that do not match `students_selected_by` come back null;
 * they are normalised to `[]` so a form can bind any of them without a null check.
 *
 * `conversations_count` becomes `studentCount`: it is how many students the selection
 * resolved to, which is what the overview shows.
 */
export const conversationInvitationFromDto = (
  dto: ConversationInvitationResource,
): ConversationInvitation => ({
  id: dto.id,
  name: dto.name,
  description: dto.description ?? null,
  date: parseSchoolDateTime(dto.date),
  endDate: parseSchoolDateTime(dto.end_date),
  subjectShortage: dto.subject_shortage,
  conversationType: {
    id: dto.conversation_type.id,
    name: dto.conversation_type.name,
  },
  studentsSelectedBy: dto.students_selected_by,
  educationLevels: dto.education_levels ?? [],
  mentorClasses: dto.mentor_classes ?? [],
  lessonGroups: dto.lesson_groups ?? [],
  studentIds: dto.student_ids ?? [],
  status: dto.status,
  canEdit: dto.can_edit,
  canDelete: dto.can_delete,
  studentCount: dto.conversations_count,
  studentTotalCount: dto.conversations_total_count,
  preparedCount: dto.conversations_prepared_count,
  preparedPercentage: percentageOf(
    dto.conversations_prepared_count,
    dto.conversations_count,
  ),
  // Not in the payload yet: the backend is adding a computed status per gesprek. Until its
  // count travels with the invitation, the Gesprekken-afgerond card reads 0 of N.
  completedCount: 0,
  completedPercentage: percentageOf(0, dto.conversations_count),
  inProgress: dto.in_progress,
  attachmentAllowed: Boolean(dto.attachment_allowed),
  attachmentRequired: Boolean(dto.attachment_required),
  questions: conversationQuestionsFromDto(dto.questions),
  attachments: mapAttachments(dto.attachments),
  links: mapLinks(dto.links),
  videos: mapVideos(dto.videos),
});

/**
 * Maps a row of `GET api/conversation-invitations/{id}/conversations` — one leerling of an
 * ingepland gesprek, with the gesprek of their own the row links to.
 */
export const conversationInvitationStudentFromDto = (
  dto: ConversationInvitationStudentResource,
): ConversationInvitationStudentRow => ({
  conversationId: dto.id,
  student: {
    id: dto.student.id,
    name: dto.student.name,
    avatar: dto.student.avatar,
    mentorClass: dto.student.mentor_class ?? null,
  },
  isPrepared: dto.is_prepared,
  status: dto.status,
});
